import { useCallback } from 'react';
import { hasKey } from 'src/tools/object.tools';
import { fuzzyFinder, strictFinder } from 'src/tools/string.tools';

import { RecordObject, Maybe, AnyValue } from 'src/models/general.model';

// TODO: add tests
/**
 * Hook to perform fuzzy finding in objects and arrays of objects
 */
export function useFuzzyFinder(keysToSearch: string[], isStrictSearch = false) {
  const finder = isStrictSearch ? strictFinder : fuzzyFinder;
  // at least 1 key should be string, should be present in the object

  const checkKeys = useCallback(
    <T extends RecordObject<AnyValue>>(obj: T): boolean => {
      let result = false;

      for (const key of keysToSearch) {
        if (hasKey(key, obj)) {
          result = true;
          break;
        }
      }

      return result;
    },
    [keysToSearch],
  );

  const findInObject = useCallback(
    <T extends RecordObject<AnyValue>>(searchString: string, obj: T): boolean => {
      let result = false;

      for (const key of keysToSearch) {
        if (finder(searchString, String(obj[key]))) {
          result = true;
          break;
        }
      }

      return result;
    },
    [finder, keysToSearch],
  );

  // if search symbols are present -> return object
  const makeDataToRender = useCallback(
    <T extends RecordObject<AnyValue>>(searchString: string, data: T[] | T): Maybe<T> => {
      if (Array.isArray(data)) return;

      if (findInObject(searchString, data as T)) return data as T;
    },
    [findInObject],
  );

  // if search symbols are present -> return object array
  const makeArrayDataToRender = useCallback(
    <T extends RecordObject<AnyValue>>(searchString: Maybe<string>, data: T[] | T): T[] => {
      if (!Array.isArray(data)) return [];

      if (!searchString) return data;

      let result: T[] = [];

      for (const element of data as T[]) {
        if (checkKeys(element) && findInObject(searchString, element)) result = [...result, element];
      }

      return result;
    },
    [checkKeys, findInObject],
  );

  return { makeArrayDataToRender, makeDataToRender };
}
